import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Apple,
  ArrowForward,
  AutoAwesome,
  CheckCircle,
  CloudDone,
  Description,
  PeopleAlt,
  Security,
  ShowChart,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { BRAND } from '../config/brand';
import { translatePublicSite } from '../i18n/publicSiteText';

const hindiCampaignTranslations: Record<string, string> = {
  'Kundlistar Studio for iOS | Astrology for iPhone & iPad': 'iOS के लिए Kundlistar Studio | iPhone और iPad पर ज्योतिष',
  'Explore Vedic, KP, Nadi, BNN, Numerology, Panchang and more in Kundlistar Studio for iPhone and iPad. Premium is ₹199/month or ₹999/year in the app.':
    'iPhone और iPad के लिए Kundlistar Studio में वैदिक, KP, नाडी, BNN, अंक ज्योतिष, पंचांग और बहुत कुछ देखें। ऐप में प्रीमियम ₹199/माह या ₹999/वर्ष है।',
  'Kundlistar Studio, Vedic astrology iPhone, KP astrology iPhone, Nadi astrology app, BNN astrology, numerology iOS, Lo Shu Grid, ₹999 astrology app':
    'Kundlistar Studio, वैदिक ज्योतिष iPhone, KP ज्योतिष iPhone, नाडी ज्योतिष ऐप, BNN ज्योतिष, अंक ज्योतिष iOS, लो शू ग्रिड, ₹999 ज्योतिष ऐप',
  'Vedic, KP, Nadi, BNN, Numerology and more': 'वैदिक, KP, नाडी, BNN, अंक ज्योतिष और बहुत कुछ',
  'Multiple astrology systems—on iPhone and iPad.': 'कई ज्योतिष प्रणालियाँ—iPhone और iPad पर।',
  'Study Vedic astrology, KP, Nadi, BNN, Jaimini, Western astrology, Vedic Numerology, Lo Shu Grid, Panchang and more in one focused iOS app for iPhone and iPad.':
    'iPhone और iPad के लिए एक ही सुव्यवस्थित iOS ऐप में वैदिक ज्योतिष, KP, नाडी, BNN, जैमिनी, वेस्टर्न ज्योतिष, वैदिक अंक ज्योतिष, लो शू ग्रिड, पंचांग और बहुत कुछ पढ़ें।',
  'Yearly Premium': 'वार्षिक प्रीमियम',
  'Monthly Premium': 'मासिक प्रीमियम',
  'Launch price · 50% off': 'लॉन्च मूल्य · 50% छूट',
  'Locked for lifetime for the first 500 users': 'पहले 500 उपयोगकर्ताओं के लिए जीवनभर यही मूल्य',
  'Try on web': 'वेब पर आज़माएँ',
  'Native iOS app for iPhone and iPad': 'iPhone और iPad के लिए नेटिव iOS ऐप',
  'Apple-managed billing': 'Apple द्वारा प्रबंधित बिलिंग',
  'PROFESSIONAL ASTROLOGY WORKSPACE': 'पेशेवर ज्योतिष कार्यक्षेत्र',
  'Astrology systems': 'ज्योतिष प्रणालियाँ',
  'One app. Deeper analysis.': 'एक ऐप। गहरा विश्लेषण।',
  'Vedic': 'वैदिक',
  'Nadi & BNN': 'नाडी और BNN',
  'Numerology': 'अंक ज्योतिष',
  'Vedic, Nadi & BNN': 'वैदिक, नाडी और BNN',
  'KP & Ruling Planets': 'KP और रूलिंग प्लैनेट्स',
  'Lo Shu Grid & Numerology': 'लो शू ग्रिड और अंक ज्योतिष',
  'ASTROLOGY WITHOUT LIMITS': 'सीमाओं के बिना ज्योतिष',
  'Go beyond a basic Kundli app.': 'एक सामान्य कुंडली ऐप से आगे बढ़ें।',
  'Vedic Astrology': 'वैदिक ज्योतिष',
  'Create saved charts and work through the Vedic analysis available in Studio.':
    'चार्ट सेव करें और Studio में उपलब्ध वैदिक विश्लेषण पर काम करें।',
  'KP Astrology': 'KP ज्योतिष',
  'Use KP analysis alongside Ruling Planets and Horary workflows.':
    'रूलिंग प्लैनेट्स और प्रश्न कुंडली वर्कफ़्लो के साथ KP विश्लेषण का उपयोग करें।',
  'Dedicated Nadi and Bhrigu Nandi Nadi chart modules.': 'नाडी और भृगु नंदी नाडी के लिए समर्पित चार्ट मॉड्यूल।',
  'Vedic Numerology and Lo Shu Grid tools are included.': 'वैदिक अंक ज्योतिष और लो शू ग्रिड टूल शामिल हैं।',
  'SYSTEMS & TOOLS INCLUDED': 'शामिल प्रणालियाँ और टूल',
  'Explore more astrology from one iOS app.': 'एक iOS ऐप से अधिक ज्योतिष प्रणालियाँ देखें।',
  'Move from core chart analysis to specialised systems, Numerology and timing tools—then use the built-in CMS to manage clients and consultations.':
    'मुख्य चार्ट विश्लेषण से विशेष प्रणालियों, अंक ज्योतिष और समय टूल तक जाएँ—फिर क्लाइंट और परामर्श संभालने के लिए बिल्ट-इन CMS का उपयोग करें।',
  'Core Astrology': 'मुख्य ज्योतिष',
  'Chart systems': 'चार्ट प्रणालियाँ',
  'Vedic analysis': 'वैदिक विश्लेषण',
  'KP analysis': 'KP विश्लेषण',
  'Nadi analysis': 'नाडी विश्लेषण',
  'BNN chart module': 'BNN चार्ट मॉड्यूल',
  'Additional Systems': 'अतिरिक्त प्रणालियाँ',
  'More ways to study a chart': 'चार्ट का अध्ययन करने के अधिक तरीके',
  'Jaimini astrology': 'जैमिनी ज्योतिष',
  'Western chart module': 'वेस्टर्न चार्ट मॉड्यूल',
  'Lal Kitab': 'लाल किताब',
  'Panch Pakshi': 'पंच पक्षी',
  'Number-based tools': 'अंक-आधारित टूल',
  'Vedic Numerology': 'वैदिक अंक ज्योतिष',
  'Lo Shu Grid': 'लो शू ग्रिड',
  'Birth-date calculations': 'जन्म-तिथि गणनाएँ',
  'Grid patterns and number balance': 'ग्रिड पैटर्न और अंकों का संतुलन',
  'Timing & Utilities': 'समय और उपयोगी टूल',
  'Plan with astrological timing': 'ज्योतिषीय समय के अनुसार योजना बनाएँ',
  'Daily Panchang': 'दैनिक पंचांग',
  'Ruling Planets': 'रूलिंग प्लैनेट्स',
  'Transit tools': 'गोचर टूल',
  'Horary': 'प्रश्न कुंडली',
  'Charts & Records': 'चार्ट और रिकॉर्ड',
  'Save and use your work': 'अपने काम को सेव करें और उपयोग करें',
  'Unlimited chart saves': 'असीमित चार्ट सेव',
  'Client notes and relations': 'क्लाइंट नोट्स और संबंध',
  'PDF downloads for saved client charts': 'सेव किए गए क्लाइंट चार्ट की PDF डाउनलोड',
  'Private consultation records': 'निजी परामर्श रिकॉर्ड',
  'CMS & Practice Tools': 'CMS और प्रैक्टिस टूल',
  'Manage your work after analysis': 'विश्लेषण के बाद अपना काम व्यवस्थित करें',
  'Client profiles & linked charts': 'क्लाइंट प्रोफाइल और लिंक किए गए चार्ट',
  'Planner, follow-ups & tasks': 'प्लानर, फॉलो-अप और कार्य',
  'Notes & consultation history': 'नोट्स और परामर्श इतिहास',
  'Relations, reminders & report records': 'संबंध, रिमाइंडर और रिपोर्ट रिकॉर्ड',
  'SIMPLE IOS PRICING': 'सरल iOS मूल्य',
  'Choose monthly or yearly Premium.': 'मासिक या वार्षिक प्रीमियम चुनें।',
  'Both plans unlock the same Premium workspace. Purchase securely inside the Kundlistar Studio iOS app on iPhone or iPad.':
    'दोनों प्लान एक ही प्रीमियम कार्यक्षेत्र अनलॉक करते हैं। iPhone या iPad पर Kundlistar Studio iOS ऐप में सुरक्षित रूप से खरीदें।',
  'Flexible access to every Kundlistar Studio Premium feature.':
    'Kundlistar Studio की हर प्रीमियम सुविधा का लचीला एक्सेस।',
  'Full-year Premium access at the best available value.': 'सबसे किफायती मूल्य पर पूरे वर्ष का प्रीमियम एक्सेस।',
  'Best Value': 'सर्वोत्तम मूल्य',
  '/ month': '/ माह',
  '/ year': '/ वर्ष',
  '/month': '/माह',
  '/yr': '/वर्ष',
  'Vedic, KP, Nadi and BNN': 'वैदिक, KP, नाडी और BNN',
  'Numerology and Lo Shu Grid': 'अंक ज्योतिष और लो शू ग्रिड',
  'CMS and saved-chart PDF downloads': 'CMS और सेव किए गए चार्ट की PDF डाउनलोड',
  'Apple-managed subscription': 'Apple द्वारा प्रबंधित सदस्यता',
  'Get Premium in the iOS app': 'iOS ऐप में प्रीमियम लें',
  'Payment, renewal and cancellation are handled by Apple. App Store terms apply.':
    'भुगतान, नवीनीकरण और रद्द करना Apple द्वारा संभाला जाता है। App Store की शर्तें लागू होती हैं।',
  'WHY KUNDLISTAR STUDIO': 'KUNDLISTAR STUDIO क्यों',
  'More systems. One iOS app.': 'अधिक प्रणालियाँ। एक iOS ऐप।',
  'Kundlistar Studio brings major Indian astrology systems and Numerology together, with CMS tools available when you want to turn analysis into an organised practice.':
    'Kundlistar Studio प्रमुख भारतीय ज्योतिष प्रणालियों और अंक ज्योतिष को एक साथ लाता है। विश्लेषण को व्यवस्थित प्रैक्टिस में बदलने के लिए CMS टूल भी उपलब्ध हैं।',
  'Deep astrology systems': 'गहन ज्योतिष प्रणालियाँ',
  'Work across Vedic, KP, Nadi, BNN, Numerology and advanced supporting tools.':
    'वैदिक, KP, नाडी, BNN, अंक ज्योतिष और उन्नत सहायक टूल पर काम करें।',
  'Charts and timing tools': 'चार्ट और समय टूल',
  'Move from chart calculation to transits, Panchang, Ruling Planets and Horary.':
    'चार्ट गणना से गोचर, पंचांग, रूलिंग प्लैनेट्स और प्रश्न कुंडली तक जाएँ।',
  'CMS when you need it': 'ज़रूरत के समय CMS',
  'Organise clients, consultations, notes and follow-ups after your analysis.':
    'अपने विश्लेषण के बाद क्लाइंट, परामर्श, नोट्स और फॉलो-अप व्यवस्थित करें।',
  'QUESTIONS BEFORE YOU BEGIN?': 'शुरू करने से पहले प्रश्न?',
  'Everything you need to know.': 'वह सब जो आपको जानना चाहिए।',
  'Where do I purchase Premium?': 'मैं प्रीमियम कहाँ से खरीदूँ?',
  'Download Kundlistar Studio from the Apple App Store on iPhone or iPad and purchase ₹199 monthly or ₹999 yearly Premium inside the iOS app.':
    'iPhone या iPad पर Apple App Store से Kundlistar Studio डाउनलोड करें और iOS ऐप में ₹199 मासिक या ₹999 वार्षिक प्रीमियम खरीदें।',
  'Do both plans include the same features?': 'क्या दोनों प्लान में समान सुविधाएँ हैं?',
  'Yes. Monthly and yearly Premium unlock the same Studio workspace. The yearly plan provides better long-term value.':
    'हाँ। मासिक और वार्षिक प्रीमियम एक ही Studio कार्यक्षेत्र अनलॉक करते हैं। वार्षिक प्लान लंबे समय के लिए अधिक किफायती है।',
  'Can professional astrologers use Kundlistar Studio?': 'क्या पेशेवर ज्योतिषी Kundlistar Studio का उपयोग कर सकते हैं?',
  'Yes. Along with its astrology systems, Studio includes client profiles, linked charts, planning, tasks, reminders, notes and consultation records.':
    'हाँ। ज्योतिष प्रणालियों के साथ Studio में क्लाइंट प्रोफाइल, लिंक किए गए चार्ट, प्लानिंग, कार्य, रिमाइंडर, नोट्स और परामर्श रिकॉर्ड शामिल हैं।',
  'How is the subscription managed?': 'सदस्यता कैसे प्रबंधित होती है?',
  'Apple manages payment, renewal and cancellation through the Apple ID used in the App Store.':
    'Apple, App Store में उपयोग की गई Apple ID के माध्यम से भुगतान, नवीनीकरण और रद्द करना प्रबंधित करता है।',
  'Your astrology app for iPhone and iPad is ready.': 'iPhone और iPad के लिए आपका ज्योतिष ऐप तैयार है।',
  'Download Kundlistar Studio and explore Vedic, KP, Nadi, BNN, Numerology, Panchang, Ruling Planets, Transit tools and more in one place.':
    'Kundlistar Studio डाउनलोड करें और वैदिक, KP, नाडी, BNN, अंक ज्योतिष, पंचांग, रूलिंग प्लैनेट्स, गोचर टूल और बहुत कुछ एक ही जगह देखें।',
  'View iOS plans and details': 'iOS प्लान और विवरण देखें',
  'Premium': 'प्रीमियम',
};

const astrologyProof = [
  {
    title: 'Vedic Astrology',
    body: 'Create saved charts and work through the Vedic analysis available in Studio.',
    icon: <AutoAwesome />,
  },
  {
    title: 'KP Astrology',
    body: 'Use KP analysis alongside Ruling Planets and Horary workflows.',
    icon: <ShowChart />,
  },
  {
    title: 'Nadi & BNN',
    body: 'Dedicated Nadi and Bhrigu Nandi Nadi chart modules.',
    icon: <CloudDone />,
  },
  {
    title: 'Numerology',
    body: 'Vedic Numerology and Lo Shu Grid tools are included.',
    icon: <Description />,
  },
];

const moduleGroups = [
  {
    title: 'Core Astrology',
    eyebrow: 'Chart systems',
    items: ['Vedic analysis', 'KP analysis', 'Nadi analysis', 'BNN chart module'],
  },
  {
    title: 'Additional Systems',
    eyebrow: 'More ways to study a chart',
    items: ['Jaimini astrology', 'Western chart module', 'Lal Kitab', 'Panch Pakshi'],
  },
  {
    title: 'Numerology',
    eyebrow: 'Number-based tools',
    items: ['Vedic Numerology', 'Lo Shu Grid', 'Birth-date calculations', 'Grid patterns and number balance'],
  },
  {
    title: 'Timing & Utilities',
    eyebrow: 'Plan with astrological timing',
    items: ['Daily Panchang', 'Ruling Planets', 'Transit tools', 'Horary'],
  },
  {
    title: 'Charts & Records',
    eyebrow: 'Save and use your work',
    items: ['Unlimited chart saves', 'Client notes and relations', 'PDF downloads for saved client charts', 'Private consultation records'],
  },
  {
    title: 'CMS & Practice Tools',
    eyebrow: 'Manage your work after analysis',
    items: ['Client profiles & linked charts', 'Planner, follow-ups & tasks', 'Notes & consultation history', 'Relations, reminders & report records'],
  },
];

const plans = [
  {
    name: 'Monthly Premium',
    price: '₹199',
    period: '/ month',
    description: 'Flexible access to every Kundlistar Studio Premium feature.',
  },
  {
    name: 'Yearly Premium',
    price: '₹999',
    period: '/ year',
    description: 'Full-year Premium access at the best available value.',
    badge: 'Best Value',
  },
];

const faqs = [
  {
    question: 'Where do I purchase Premium?',
    answer: 'Download Kundlistar Studio from the Apple App Store on iPhone or iPad and purchase ₹199 monthly or ₹999 yearly Premium inside the iOS app.',
  },
  {
    question: 'Do both plans include the same features?',
    answer: 'Yes. Monthly and yearly Premium unlock the same Studio workspace. The yearly plan provides better long-term value.',
  },
  {
    question: 'Can professional astrologers use Kundlistar Studio?',
    answer: 'Yes. Along with its astrology systems, Studio includes client profiles, linked charts, planning, tasks, reminders, notes and consultation records.',
  },
  {
    question: 'How is the subscription managed?',
    answer: 'Apple manages payment, renewal and cancellation through the Apple ID used in the App Store.',
  },
];

const trackAppStoreClick = (placement: string) => {
  const browserWindow = window as Window & { dataLayer?: Array<Record<string, unknown>> };
  browserWindow.dataLayer = browserWindow.dataLayer || [];
  browserWindow.dataLayer.push({
    event: 'app_store_click',
    app: 'kundlistar_studio',
    placement,
  });
};

const AppStoreButton: React.FC<{ placement: string; fullWidth?: boolean; label?: string }> = ({
  placement,
  fullWidth = false,
  label = 'Download on the App Store',
}) => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const text = (english: string) => translatePublicSite(language, english, hindiCampaignTranslations[english]);

  return (
  <Button
    component="a"
    href={BRAND.appStoreUrl}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => trackAppStoreClick(placement)}
    variant="contained"
    size="large"
    fullWidth={fullWidth}
    startIcon={<Apple />}
    endIcon={<ArrowForward />}
    sx={{
      bgcolor: '#10251a',
      color: '#fff',
      px: 3,
      py: 1.35,
      fontWeight: 950,
      borderRadius: 2.5,
      boxShadow: '0 14px 30px rgba(16,37,26,0.2)',
      '&:hover': { bgcolor: '#1c4431' },
    }}
  >
    {text(label)}
  </Button>
  );
};

const CampaignLandingPage: React.FC = () => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const text = (english: string) => translatePublicSite(language, english, hindiCampaignTranslations[english]);

  return (
  <Box sx={{ bgcolor: '#fbfaf5', color: '#10251a', minHeight: '100vh', overflow: 'hidden', textAlign: 'left' }}>
    <SEO
      title={text('Kundlistar Studio for iOS | Astrology for iPhone & iPad')}
      description={text('Explore Vedic, KP, Nadi, BNN, Numerology, Panchang and more in Kundlistar Studio for iPhone and iPad. Premium is ₹199/month or ₹999/year in the app.')}
      path="/start"
      image="/promo-banner.png"
      keywords={text('Kundlistar Studio, Vedic astrology iPhone, KP astrology iPhone, Nadi astrology app, BNN astrology, numerology iOS, Lo Shu Grid, ₹999 astrology app')}
    />

    <Box component="main">
      <Box
        sx={{
          position: 'relative',
          pt: { xs: 3.5, sm: 5, md: 9 },
          pb: { xs: 5, md: 11 },
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 520,
            height: 520,
            borderRadius: '50%',
            border: '1px solid rgba(191,145,42,0.18)',
            top: -290,
            right: -110,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 4.5, md: 8 }} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Chip
                icon={<AutoAwesome sx={{ color: '#9b7421 !important' }} />}
                label={text('Vedic, KP, Nadi, BNN, Numerology and more')}
                variant="outlined"
                sx={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderColor: 'rgba(184,137,32,0.38)',
                  bgcolor: '#fffaf0',
                  color: '#6f541c',
                  fontWeight: 850,
                  mb: { xs: 2.25, sm: 3 },
                  '& .MuiChip-label': {
                    display: 'block',
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.75, sm: 0.5 },
                    whiteSpace: 'normal',
                    lineHeight: 1.25,
                    fontSize: { xs: 13, sm: 14 },
                  },
                  '& .MuiChip-icon': { ml: 1 },
                }}
              />
              <Typography
                component="h1"
                sx={{
                  fontFamily: 'Georgia, Times New Roman, serif',
                  fontSize: { xs: '2.15rem', sm: '3.5rem', md: '4.35rem' },
                  lineHeight: { xs: 1.04, md: 1.02 },
                  letterSpacing: { xs: '-0.035em', md: '-0.045em' },
                  fontWeight: 900,
                  textAlign: 'left',
                }}
              >
                {text('Multiple astrology systems—on iPhone and iPad.')}
              </Typography>
              <Typography sx={{ mt: { xs: 2, sm: 3 }, color: '#52645a', fontSize: { xs: 16, sm: 19 }, lineHeight: { xs: 1.6, sm: 1.75 }, maxWidth: 680 }}>
                {text('Study Vedic astrology, KP, Nadi, BNN, Jaimini, Western astrology, Vedic Numerology, Lo Shu Grid, Panchang and more in one focused iOS app for iPhone and iPad.')}
              </Typography>

              <Paper
                component="a"
                href="#pricing"
                elevation={0}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  gap: { sm: 1.75 },
                  width: { xs: '100%', sm: 'auto' },
                  mt: { xs: 2.75, sm: 3.5 },
                  px: { xs: 1.4, sm: 2.25 },
                  py: { xs: 1.25, sm: 1.4 },
                  borderRadius: 3,
                  border: '1px solid rgba(184,137,32,0.38)',
                  bgcolor: '#fff7df',
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                <Typography
                  sx={{
                    color: '#6f541c',
                    fontWeight: 900,
                    fontSize: { xs: 13, sm: 16 },
                    textTransform: { xs: 'uppercase', sm: 'none' },
                    letterSpacing: { xs: '0.08em', sm: 0 },
                    width: { xs: '100%', sm: 'auto' },
                    mb: { xs: 0.9, sm: 0 },
                  }}
                >
                  {text('Premium')}
                </Typography>
                <Box sx={{ minWidth: 0, flex: { xs: '1 1 60%', sm: '0 1 auto' } }}>
                  <Typography variant="caption" sx={{ display: 'block', color: '#806321', fontWeight: 850, lineHeight: 1.1 }}>
                    {text('Yearly Premium')}
                  </Typography>
                  <Stack direction="row" spacing={0.55} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.55 }}>
                    <Typography
                      component="del"
                      sx={{ color: '#8b5f52', fontSize: { xs: 12, sm: 15 }, fontWeight: 800, textDecorationThickness: '2px' }}
                    >
                      ₹1,999{text('/yr')}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        px: 0.85,
                        py: 0.25,
                        borderRadius: 10,
                        bgcolor: '#b88920',
                        color: '#fff',
                        fontSize: { xs: 8.5, sm: 11 },
                        fontWeight: 900,
                        letterSpacing: { xs: 0, sm: '0.025em' },
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {text('Launch price · 50% off')}
                    </Box>
                  </Stack>
                  <Box sx={{ mt: 0.4 }}>
                    <Typography component="span" sx={{ color: '#143d28', fontWeight: 950, fontSize: 29, lineHeight: 1 }}>
                      ₹999
                    </Typography>
                    <Typography component="span" sx={{ ml: 0.45, color: '#65736b', fontWeight: 750 }}>{text('/yr')}</Typography>
                  </Box>
                  <Typography sx={{ mt: 0.4, color: '#6f541c', fontSize: { xs: 9.5, sm: 12 }, fontWeight: 850, lineHeight: 1.25 }}>
                    {text('Locked for lifetime for the first 500 users')}
                  </Typography>
                </Box>
                <Box
                  aria-hidden="true"
                  sx={{
                    alignSelf: 'stretch',
                    mx: { xs: 1.1, sm: 0 },
                    borderLeft: '1px solid rgba(128,99,33,0.28)',
                  }}
                />
                <Box sx={{ minWidth: 0, flex: { xs: '1 1 32%', sm: '0 0 155px' } }}>
                  <Typography variant="caption" sx={{ display: 'block', color: '#806321', fontWeight: 850, lineHeight: 1.1 }}>
                    {text('Monthly Premium')}
                  </Typography>
                  <Typography component="span" sx={{ color: '#143d28', fontWeight: 950, fontSize: { xs: 26, sm: 29 }, lineHeight: 1 }}>
                    ₹199
                  </Typography>
                  <Typography component="span" sx={{ ml: 0.35, color: '#65736b', fontSize: { xs: 14, sm: 16 }, fontWeight: 750 }}>{text('/month')}</Typography>
                </Box>
              </Paper>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mt: 3 }}>
                <AppStoreButton placement="campaign_hero" />
                <Button
                  component="a"
                  href="https://kundlistarstudio.com/calculate"
                  variant="outlined"
                  size="large"
                  startIcon={<ShowChart />}
                  sx={{ borderColor: '#143d28', color: '#143d28', fontWeight: 900 }}
                >
                  {text('Try on web')}
                </Button>
                <Button component="a" href="#pricing" variant="outlined" size="large" sx={{ borderColor: '#143d28', color: '#143d28', fontWeight: 900 }}>
                  {text('See pricing')}
                </Button>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.75, sm: 2 }} sx={{ mt: 2.25, color: '#65736b' }}>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <CheckCircle sx={{ fontSize: 17, color: '#2d6a4f' }} /> {text('Native iOS app for iPhone and iPad')}
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Security sx={{ fontSize: 17, color: '#2d6a4f' }} /> {text('Apple-managed billing')}
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  borderRadius: { xs: 5, md: 7 },
                  background: 'radial-gradient(circle at 75% 18%, rgba(230,190,91,0.3), transparent 30%), linear-gradient(145deg, #071b12, #0d3b27 68%, #1b5a3c)',
                  boxShadow: '0 34px 80px rgba(12,49,33,0.26)',
                  p: { xs: 2.5, sm: 4 },
                  color: '#fff',
                  minHeight: { xs: 430, sm: 520 },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.2}>
                  <Box component="img" src="/logo.png" alt="" sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#fff', objectFit: 'contain' }} />
                  <Box>
                    <Typography sx={{ fontFamily: 'Georgia, serif', fontWeight: 900, fontSize: 20 }}>Kundlistar Studio</Typography>
                    <Typography sx={{ color: '#dfc271', fontSize: 10, letterSpacing: '0.15em' }}>{text('PROFESSIONAL ASTROLOGY WORKSPACE')}</Typography>
                  </Box>
                </Stack>
                <Paper elevation={0} sx={{ mt: 4, p: { xs: 2, sm: 3 }, borderRadius: 4, bgcolor: '#f7faf8' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" sx={{ color: '#718077' }}>{text('Astrology systems')}</Typography>
                      <Typography sx={{ color: '#143d28', fontWeight: 950, fontSize: { xs: 22, sm: 27 } }}>{text('One app. Deeper analysis.')}</Typography>
                    </Box>
                    <AutoAwesome sx={{ color: '#b88920' }} />
                  </Stack>
                  <Grid container spacing={1.2} sx={{ mt: 2 }}>
                    {[
                      ['Vedic', <AutoAwesome />],
                      ['KP', <ShowChart />],
                      ['Nadi & BNN', <CloudDone />],
                      ['Numerology', <Description />],
                    ].map(([label, icon]) => (
                      <Grid key={String(label)} size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ p: 1.6, textAlign: 'center', color: '#143d28', borderColor: '#dfe9e2' }}>
                          <Box sx={{ color: '#2d6a4f' }}>{icon}</Box>
                          <Typography sx={{ mt: 0.5, fontWeight: 850, fontSize: 13 }}>{text(String(label))}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                  <Divider sx={{ my: 2.5 }} />
                  <Stack spacing={1.1}>
                    {['Vedic, Nadi & BNN', 'KP & Ruling Planets', 'Lo Shu Grid & Numerology'].map((item) => (
                      <Stack key={item} direction="row" spacing={1} alignItems="center">
                        <CheckCircle sx={{ color: '#2d6a4f', fontSize: 18 }} />
                        <Typography sx={{ color: '#4d6155', fontSize: 13 }}>{text(item)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ bgcolor: '#fff', py: { xs: 6, md: 8 }, borderBlock: '1px solid #eee8d9' }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: '#9b7421', fontWeight: 900, letterSpacing: '0.14em' }}>{text('ASTROLOGY WITHOUT LIMITS')}</Typography>
          <Typography variant="h3" sx={{ mt: 1, fontWeight: 900, color: '#10251a' }}>{text('Go beyond a basic Kundli app.')}</Typography>
          <Grid container spacing={2.5} sx={{ mt: 2.5 }}>
            {astrologyProof.map((item) => (
              <Grid key={item.title} size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined" sx={{ height: '100%', borderColor: '#dfe7e1', borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ color: '#2d6a4f', mb: 1.5 }}>{item.icon}</Box>
                    <Typography sx={{ fontWeight: 900, fontSize: 19 }}>{text(item.title)}</Typography>
                    <Typography sx={{ mt: 1, color: '#627067', lineHeight: 1.65 }}>{text(item.body)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 9 } }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: '#9b7421', fontWeight: 900, letterSpacing: '0.14em' }}>{text('SYSTEMS & TOOLS INCLUDED')}</Typography>
          <Typography variant="h3" sx={{ mt: 1, fontWeight: 900 }}>{text('Explore more astrology from one iOS app.')}</Typography>
          <Typography sx={{ mt: 1.5, color: '#627067', maxWidth: 760, lineHeight: 1.7 }}>
            {text('Move from core chart analysis to specialised systems, Numerology and timing tools—then use the built-in CMS to manage clients and consultations.')}
          </Typography>
          <Grid container spacing={2.5} sx={{ mt: 3 }}>
            {moduleGroups.map((group) => (
              <Grid key={group.title} size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ height: '100%', p: { xs: 3, sm: 4 }, borderRadius: 4, borderColor: '#dfe7e1', bgcolor: '#fff' }}>
                  <Typography variant="caption" sx={{ color: '#9b7421', fontWeight: 900, letterSpacing: '0.08em' }}>{text(group.eyebrow)}</Typography>
                  <Typography sx={{ mt: 0.7, fontWeight: 950, fontSize: 24 }}>{text(group.title)}</Typography>
                  <Grid container spacing={1.2} sx={{ mt: 1.5 }}>
                    {group.items.map((item) => (
                      <Grid key={item} size={{ xs: 12, sm: 6 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <CheckCircle sx={{ color: '#2d6a4f', fontSize: 19, mt: 0.2 }} />
                          <Typography sx={{ color: '#53645a', lineHeight: 1.55 }}>{text(item)}</Typography>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box id="pricing" sx={{ py: { xs: 6, md: 9 }, bgcolor: '#eef5f0', borderBlock: '1px solid #dfe8e1', scrollMarginTop: 90 }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: '#9b7421', fontWeight: 900, letterSpacing: '0.14em' }}>{text('SIMPLE IOS PRICING')}</Typography>
          <Typography variant="h3" sx={{ mt: 1, fontWeight: 950 }}>{text('Choose monthly or yearly Premium.')}</Typography>
          <Typography sx={{ mt: 1.5, color: '#5c6d62', maxWidth: 720, lineHeight: 1.7 }}>
            {text('Both plans unlock the same Premium workspace. Purchase securely inside the Kundlistar Studio iOS app on iPhone or iPad.')}
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2.5 }} justifyContent="center">
            {plans.map((plan) => (
              <Grid key={plan.name} size={{ xs: 12, md: 5 }}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: '2px solid',
                    borderColor: plan.badge ? '#2d6a4f' : '#d4e0d8',
                    boxShadow: plan.badge ? '0 18px 45px rgba(45,106,79,0.16)' : 'none',
                    position: 'relative',
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                    {plan.badge && <Chip label={text(plan.badge)} color="primary" size="small" sx={{ mb: 2, fontWeight: 900 }} />}
                    <Typography sx={{ fontWeight: 950, fontSize: 22 }}>{text(plan.name)}</Typography>
                    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ my: 2 }}>
                      <Typography sx={{ fontSize: 48, lineHeight: 1, fontWeight: 950, color: '#143d28' }}>{plan.price}</Typography>
                      <Typography sx={{ color: '#65736b', fontWeight: 750 }}>{text(plan.period)}</Typography>
                    </Stack>
                    <Typography sx={{ color: '#5b6b61', lineHeight: 1.7 }}>{text(plan.description)}</Typography>
                    <Stack spacing={1.1} sx={{ my: 3 }}>
                      {['Vedic, KP, Nadi and BNN', 'Numerology and Lo Shu Grid', 'CMS and saved-chart PDF downloads', 'Apple-managed subscription'].map((feature) => (
                        <Stack key={feature} direction="row" spacing={1} alignItems="center">
                          <CheckCircle sx={{ color: '#2d6a4f', fontSize: 20 }} />
                          <Typography sx={{ color: '#415348' }}>{text(feature)}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                    <AppStoreButton
                      placement={`campaign_pricing_${plan.name.toLowerCase().replace(/\s+/g, '_')}`}
                      fullWidth
                      label="Get Premium in the iOS app"
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" sx={{ mt: 3, color: '#66756c', textAlign: 'center' }}>
            {text('Payment, renewal and cancellation are handled by Apple. App Store terms apply.')}
          </Typography>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 9 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography variant="overline" sx={{ color: '#9b7421', fontWeight: 900, letterSpacing: '0.14em' }}>{text('WHY KUNDLISTAR STUDIO')}</Typography>
              <Typography variant="h3" sx={{ mt: 1, fontWeight: 950 }}>{text('More systems. One iOS app.')}</Typography>
              <Typography sx={{ mt: 2, color: '#5d6d63', lineHeight: 1.75 }}>
                {text('Kundlistar Studio brings major Indian astrology systems and Numerology together, with CMS tools available when you want to turn analysis into an organised practice.')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack spacing={1.5}>
                {[
                  [<AutoAwesome />, 'Deep astrology systems', 'Work across Vedic, KP, Nadi, BNN, Numerology and advanced supporting tools.'],
                  [<ShowChart />, 'Charts and timing tools', 'Move from chart calculation to transits, Panchang, Ruling Planets and Horary.'],
                  [<PeopleAlt />, 'CMS when you need it', 'Organise clients, consultations, notes and follow-ups after your analysis.'],
                ].map(([icon, title, body]) => (
                  <Paper key={String(title)} variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: '#dfe7e1' }}>
                    <Stack direction="row" spacing={1.7} alignItems="flex-start">
                      <Box sx={{ color: '#2d6a4f', mt: 0.2 }}>{icon}</Box>
                      <Box>
                        <Typography sx={{ fontWeight: 900 }}>{text(String(title))}</Typography>
                        <Typography sx={{ mt: 0.5, color: '#637168', lineHeight: 1.6 }}>{text(String(body))}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 9 } }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ color: '#9b7421', fontWeight: 900, letterSpacing: '0.14em' }}>{text('QUESTIONS BEFORE YOU BEGIN?')}</Typography>
          <Typography variant="h3" sx={{ mt: 1, mb: 3, fontWeight: 950 }}>{text('Everything you need to know.')}</Typography>
          <Stack spacing={1.5}>
            {faqs.map((faq) => (
              <Paper key={faq.question} variant="outlined" sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 3, borderColor: '#dfe7e1', bgcolor: '#fff' }}>
                <Typography sx={{ fontWeight: 900, fontSize: 18 }}>{text(faq.question)}</Typography>
                <Typography sx={{ mt: 1, color: '#5f6f65', lineHeight: 1.7 }}>{text(faq.answer)}</Typography>
              </Paper>
            ))}
          </Stack>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 7, md: 10 }, bgcolor: '#0d3b27', color: '#fff' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" sx={{ color: '#fff', fontWeight: 950 }}>{text('Your astrology app for iPhone and iPad is ready.')}</Typography>
          <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.78)', fontSize: 18, lineHeight: 1.7 }}>
            {text('Download Kundlistar Studio and explore Vedic, KP, Nadi, BNN, Numerology, Panchang, Ruling Planets, Transit tools and more in one place.')}
          </Typography>
          <Box sx={{ mt: 3.5, display: 'flex', justifyContent: 'center' }}>
            <AppStoreButton placement="campaign_final_cta" />
          </Box>
          <Button component={RouterLink} to="/install-iphone" sx={{ mt: 2, color: '#dfc271', fontWeight: 850 }}>
            {text('View iOS plans and details')}
          </Button>
        </Container>
      </Box>
    </Box>
  </Box>
  );
};

export default CampaignLandingPage;
